/**
 * **Firestore 쓰기 정책 — 읽기전용 모드.**
 *
 * 이 파일 하나가 «읽기전용일 때 무엇을 허용하나»를 쥔다. 사람이 만지는 건 예외 태그뿐.
 * 게이트웨이(`src/lib/firebase/firestore-write.ts`)가 모든 쓰기 진입에서 여기를 본다.
 *
 * ## 무엇을 막나
 *
 * **테스트 에이전트(`.claude/agents/journey-runner`·`parity-runner`)가 실수로 Firebase 에
 * 쓰는 것을 막는 상시 안전장치.** 전역 기본은 OFF — 프로덕션·사람 dev 는 그대로 쓴다.
 * 활성화는 그 에이전트 하니스가 서버를 띄울 때만 켠다.
 *
 * ## 두 축이 다른 곳에 산다
 *
 * - **정책(예외 태그)** = 이 const. 사람이 관리한다.
 * - **활성화(이 실행 ON/OFF)** = `NEXT_PUBLIC_FIRESTORE_READONLY` env. 에이전트별·시나리오별로
 *   달라 per-launch 라 소스 const 로 못 담는다(모두에게 같아진다). 하니스가 세팅한다.
 *   클라 가드라 브라우저 번들에 실려야 해서 `NEXT_PUBLIC_`.
 */

/**
 * 읽기전용 활성화. 하니스가 서버 기동 때 `NEXT_PUBLIC_FIRESTORE_READONLY=1` 로 켠다.
 * 세팅 안 하면(프로덕션·사람 dev) OFF — 정상 쓰기.
 */
export const FIRESTORE_READONLY = process.env.NEXT_PUBLIC_FIRESTORE_READONLY === '1';

/**
 * 서버(admin SDK) 읽기전용. admin 은 서버에서만 도므로 **서버 전용 env**다 —
 * `NEXT_PUBLIC` 아님(번들에 실릴 필요 없음). 하니스가 서버 기동 때 `FIRESTORE_ADMIN_READONLY=1`
 * 로 켠다. 켜지면 admin 쓰기 게이트웨이(`lib/firebase-admin/write`)가 admin 쓰기를 막는다.
 * ⚠️ admin 쓰기는 라우트(POST)가 때려야 도는 것이라, passive 순회엔 거의 안 걸린다(심층방어).
 */
export const FIRESTORE_ADMIN_READONLY = process.env.FIRESTORE_ADMIN_READONLY === '1';

/**
 * 읽기전용일 때도 «허용»할 쓰기 태그. 호출부가 `{ tag }` 로 이 중 하나를 달면 통과한다.
 * 기본 예외 = 부수쓰기 R op(앱이 안 깨지게 하는 최소). 그 밖의 쓰기는 읽기전용에서 막힌다.
 *
 * ⚠️ 여기에 태그를 더하는 것은 «그 쓰기를 읽기전용 순회 중에도 허용»한다는 뜻이다 — 신중히.
 */
export const READONLY_ALLOWED_TAGS: ReadonlySet<string> = new Set<string>([
  // 🔑 정책: 읽기전용에선 «모든 쓰기를 막는 게 기본»이다. 부트스트랩 쓰기는 대부분
  //    호출부가 실패를 관용(try/catch)하므로 막혀도 앱이 안 멈춘다 — 그래서 태그가 필요 없다.
  //    이 목록은 «관용으로 못 푸는 예외»만 담는다. 지금은 하나다:
  //
  //    getCheckedWorkspaceList(auth 부트스트랩)의 부수쓰기 — 읽기(WS 목록)와 쓰기가 «한 함수»에
  //    얽혀 있어, 쓰기를 막으면 읽기 결과까지 잃는다(앱이 WS 를 못 얻어 멈춘다). ① 만료 시 통짜
  //    덮어쓰기 ② 소유 WS 없으면 기본 WS 생성 — 둘 다 계정 자신의 WS 문서고, 기존 계정 순회에선
  //    갈래가 안 돈다. 그래서 «명시적 예외»로 허용한다.
  'auth-bootstrap',
  //
  // ⚠️ 나머지 부트스트랩 쓰기(setUserConnection·user.fields.update trial 등)는 태그 «없이» 막고,
  //    호출부가 실패를 삼킨다(차단+관용) — 순회 중 운영에 안 쓴다. 허용이 정말 필요하면 태그가
  //    아니라 호출부의 allowInReadonly 로 «한 번만» 연다.
]);
